import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Logo } from "./Logo";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { LanguageSwitch } from "./LanguageSwitch";
import {
  LayoutDashboard, Users, MessageSquareText, Calendar, FileText, Settings, User, Inbox, LogOut, Sparkles,
} from "lucide-react";

export const KantoorSidebar = ({ badges = {} }) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const items = [
    { to: "/dashboard", label: t("sidebar.dashboard"), icon: LayoutDashboard, key: "dashboard" },
    { to: "/dashboard/matches", label: t("sidebar.new_matches"), icon: Sparkles, key: "matches", badgeKey: "newMatches" },
    { to: "/dashboard/gesprekken", label: t("sidebar.gesprekken"), icon: MessageSquareText, key: "gesprekken", badgeKey: "newGesprekken" },
    { to: "/dashboard/klanten", label: t("sidebar.klanten"), icon: Users, key: "klanten" },
    { to: "/dashboard/agenda", label: t("sidebar.agenda"), icon: Calendar, key: "agenda" },
    { to: "/dashboard/berichten", label: t("sidebar.berichten"), icon: Inbox, key: "berichten" },
    { to: "/dashboard/facturatie", label: t("sidebar.facturatie"), icon: FileText, key: "facturatie" },
    { to: "/dashboard/profiel", label: t("sidebar.profiel"), icon: User, key: "profiel" },
    { to: "/dashboard/instellingen", label: t("sidebar.instellingen"), icon: Settings, key: "instellingen" },
  ];

  return (
    <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-slate-200 min-h-screen sticky top-0">
      <div className="p-5 border-b border-slate-100 flex items-center justify-between">
        <button onClick={() => navigate("/")} data-testid="sidebar-logo-btn">
          <Logo size="md" />
        </button>
        <LanguageSwitch />
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {items.map((it) => {
          const Icon = it.icon;
          const badge = it.badgeKey ? badges[it.badgeKey] : null;
          return (
            <NavLink key={it.key} to={it.to} end={it.to === "/dashboard"}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-[#F5A623]/10 text-[#1A2B4A]" : "text-slate-600 hover:bg-slate-50 hover:text-[#1A2B4A]"
                }`
              }
              data-testid={`sidebar-nav-${it.key}`}>
              <Icon size={18} />
              <span className="flex-1">{it.label}</span>
              {badge ? (
                <span className="bg-[#F5A623] text-white text-xs font-bold rounded-full px-2 py-0.5 min-w-[22px] text-center" data-testid={`sidebar-badge-${it.key}`}>
                  {badge}
                </span>
              ) : null}
            </NavLink>
          );
        })}
      </nav>
      <div className="p-3 border-t border-slate-100">
        <div className="flex items-center gap-3 px-3 py-2">
          {user?.picture ? (
            <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-[#1A2B4A] text-white flex items-center justify-center text-xs font-bold">
              {user?.name?.[0] || "?"}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-[#1A2B4A] truncate">{user?.name}</div>
            <div className="text-xs text-slate-500 truncate">{user?.email}</div>
          </div>
        </div>
        <button onClick={logout} className="mt-2 w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg" data-testid="sidebar-logout-btn">
          <LogOut size={16} /> {t("nav.logout")}
        </button>
      </div>
    </aside>
  );
};
